import React, { useEffect, useState } from 'react';
import { AnalyticsEvents } from 'enums';
import { captureEvent } from 'utils/analytics/captureEvent';
import { Checkbox, Icon, Text } from 'view/atoms';
import { useTranslation } from 'react-i18next';
import { ClearButton } from 'view/molecules/ClearSelectorButton';
import { TitleStyled } from 'view/molecules/MenuCategories/components/CategoriesItem/CategoriesItem.styles';
import { down } from 'static/icons/resizable';
import { ROUTES } from 'config';
import { translateCategory } from 'utils';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useCurrentUser, useMediaQuery, useUserRoleId } from 'hooks';
import { setSelectedCategory } from 'redux/modules/Categories/categories';
import { categoriesSelectors } from 'redux/modules/Categories';
import { Props } from './CategoryDropdown.model';
import * as S from './CategoryDropDown.style';

interface LocationState {
  isLogoClicked?: boolean;
  [key: string]: unknown;
}

const CategoryDropdown = ({
  icon,
  isActive,
  title,
  data,
  className,
  onClick,
  onExpandedChange,
  forceExpanded,
}: Props) => {
  const user = useCurrentUser();
  const { mobile, tablet } = useMediaQuery();

  const { i18n } = useTranslation();
  const { language } = i18n;

  const defaultSelectedCategories = useAppSelector(
    categoriesSelectors.getSelectedCategories,
  );

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded]);

  const [selectedCategories, setSelectedCategories] = useState<number[]>(
    defaultSelectedCategories ?? [],
  );

  useEffect(() => {
    setSelectedCategories(defaultSelectedCategories ?? []);
  }, [defaultSelectedCategories]);

  const navigate = useNavigate();

  const path = useLocation();
  const { discountId } = useParams();
  const isMobile = mobile || tablet;
  const locationState = path.state as LocationState | undefined;

  const dispatch = useAppDispatch();
  const roleId = useUserRoleId();

  const toggleCategory = (categoryId: number) => {
    const newSelectedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newSelectedCategories);
    dispatch(setSelectedCategory(newSelectedCategories));

    if (user?.id && roleId) {
      captureEvent(AnalyticsEvents.CategoryView, {
        user_id: String(user.id),
        category_id: String(categoryId),
        role_id: roleId,
        event_time: new Date().toISOString(),
      });
    }
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    if (!isMobile) {
      setIsExpanded(!isExpanded);
    } else if (isMobile && path.pathname !== '/main') {
      navigate(ROUTES.main);
    } else if (path.pathname === '/main') {
      setIsExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    if (!isActive && !forceExpanded) setIsExpanded(false);
  }, [isActive, forceExpanded]);

  useEffect(() => {
    if (path.pathname.startsWith(`/discount/${discountId}`)) {
      setIsExpanded(true);
    }
  }, [path.pathname]);

  useEffect(() => {
    if (locationState?.isLogoClicked) {
      setSelectedCategories([]);
      dispatch(setSelectedCategory([]));
      setIsExpanded(false);
    }
  }, [locationState?.isLogoClicked]);

  const hasSelectedCategories = selectedCategories.length > 0;

  const handleClearAll = () => {
    setSelectedCategories([]);
    dispatch(setSelectedCategory([]));
  };

  return (
    <S.ComponentWrapper
      onClick={!isMobile ? onClick : undefined}
      className='safe-to-click'
    >
      <S.ButtonWrapper
        isExpanded={isExpanded || !!forceExpanded}
        onClick={(e) => handleDropdownToggle(e)}
        className='safe-to-click'
      >
        <S.DropdownLeftWrapper className='safe-to-click'>
          <Icon src={icon} alt='all categories icon' />
          <TitleStyled>{title}</TitleStyled>
        </S.DropdownLeftWrapper>

        {isExpanded || forceExpanded ? (
          <S.FlippedIcon
            src={down}
            alt='Collapse filter icon'
            className='safe-to-click'
          />
        ) : (
          <Icon src={down} alt='Expand filter icon' className='safe-to-click' />
        )}
      </S.ButtonWrapper>
      {(isExpanded || forceExpanded) && (
        <S.DropdownInnerWrapper isExpanded={isExpanded || !!forceExpanded}>
          {data?.map((option) => (
            <S.CategoryOption
              key={option.id}
              onClick={(e) => {
                if (forceExpanded) return;
                e.stopPropagation();
                toggleCategory(option.id);
              }}
            >
              <Checkbox
                isChecked={selectedCategories.includes(option.id)}
                forSelectorOption
                onChange={() => {
                  if (forceExpanded) return;
                  toggleCategory(option.id);
                }}
              />
              <Text
                kind='body'
                customStyles={{ desktop: { fontSize: '16px' } }}
              >
                {translateCategory(option.title, language)}
              </Text>
            </S.CategoryOption>
          ))}
        </S.DropdownInnerWrapper>
      )}
      {isExpanded && hasSelectedCategories && (
        <ClearButton onClear={handleClearAll} />
      )}
    </S.ComponentWrapper>
  );
};

export default CategoryDropdown;
